import type { FormEvent } from "react";
import { useNavigate } from "react-router";

import type { FormValues } from "@prequel/import-react";
import { Alert, AlertKind, Button, ButtonStyle, Spinner } from "@prequel/shared-components";

import { DATALAKE_ROOT_KEY, useDatalakeForm } from "./formState";

import { useCreateDatalake, useUpdateDatalake } from "@/api/import/datalakes";
import ApiFormField from "@/components/ApiFormField";

type DatalakeFormProps = {
  datalakeId?: string;
  initialValues?: FormValues;
  cancelPath?: string;
};

const DatalakeForm = ({ datalakeId, initialValues, cancelPath = "/import/datalakes" }: DatalakeFormProps) => {
  const navigate = useNavigate();
  const createDatalake = useCreateDatalake();
  const updateDatalake = useUpdateDatalake();
  const isEditing = !!datalakeId;
  const form = useDatalakeForm({ initialValues, mode: isEditing ? "edit" : "create" });

  const isMutating = createDatalake.isPending || updateDatalake.isPending;
  const isPreparingForm = form.isLoading || form.isRefetching;

  // The mutation owns the success toast, cache invalidation and redirect; failures are
  // surfaced by the global mutation error handler.
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isMutating || isPreparingForm) {
      return;
    }

    if (isEditing) {
      updateDatalake.mutate({ id: datalakeId, datalake: form.getDatalakeUpdate() });
      return;
    }

    createDatalake.mutate(form.getDatalake());
  };

  if (form.isLoading) {
    return <Spinner />;
  }

  return (
    <form className="space-y-8 pb-16" onSubmit={onSubmit}>
      {!!form.error && <Alert kind={AlertKind.ERROR}>Failed to generate datalake form.</Alert>}

      <div className="space-y-6">
        {form.fields.map((field) => (
          <ApiFormField
            key={field.path}
            field={field}
            values={form.draft}
            setFieldValue={form.setFieldValue}
            rootKey={DATALAKE_ROOT_KEY}
            disabled={isMutating}
            isEditing={isEditing}
          />
        ))}
      </div>

      {!form.draft.storage && (
        <Alert kind={AlertKind.INFO}>Select a Storage type to configure connection details.</Alert>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type={ButtonStyle.TERTIARY} text="Cancel" onClick={() => navigate(cancelPath)} disabled={isMutating} />
        <Button
          type={ButtonStyle.PRIMARY}
          disabled={isMutating || isPreparingForm}
          submit
          text={
            isMutating ? (
              <div className="flex">
                <Spinner.Inline className="mr-3 text-white" />
                {isEditing ? "Updating Datalake..." : "Saving Datalake..."}
              </div>
            ) : isEditing ? (
              "Update Datalake"
            ) : (
              "Save Datalake"
            )
          }
        />
      </div>
    </form>
  );
};

export default DatalakeForm;
